import clsx from 'clsx'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

import { inscrever, obterToasts, removerToast } from '@/hooks/use-toast/store'
import type { Toast } from '@/hooks/use-toast/types'

import classes from './styles.module.scss'

const DURACAO_DA_SAIDA = 500

interface ItemDeToastProps {
  toast: Toast
}

function ItemDeToast({ toast }: ItemDeToastProps): JSX.Element {
  const [saindo, setSaindo] = useState(false)
  const [pausado, setPausado] = useState(false)

  const restanteRef = useRef(toast.duracao)
  const iniciadoEmRef = useRef(Date.now())

  const fechar = useCallback(() => {
    setSaindo(true)
  }, [])

  useEffect(() => {
    if (saindo || pausado) return

    iniciadoEmRef.current = Date.now()

    const temporizador = setTimeout(() => {
      setSaindo(true)
    }, restanteRef.current)

    return () => {
      clearTimeout(temporizador)
      restanteRef.current -= Date.now() - iniciadoEmRef.current
    }
  }, [saindo, pausado])

  useEffect(() => {
    if (!saindo) return

    const temporizador = setTimeout(() => {
      removerToast(toast.id)
    }, DURACAO_DA_SAIDA)

    return () => clearTimeout(temporizador)
  }, [saindo, toast.id])

  return (
    <li
      className={clsx(
        classes.toast,
        classes[toast.tipo],
        saindo ? classes.saindo : classes.entrando
      )}
      role={toast.tipo === 'erro' ? 'alert' : 'status'}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
      onAnimationEnd={() => {
        if (saindo) removerToast(toast.id)
      }}
    >
      {toast.icone && (
        <i className={clsx(classes.icone, toast.icone)} aria-hidden="true" />
      )}

      <div className={classes.conteudo}>
        <h2 className={classes.titulo}>{toast.titulo}</h2>
        {toast.descricao && <p className={classes.texto}>{toast.descricao}</p>}
      </div>

      <button
        type="button"
        className={classes.botaoFechar}
        onClick={fechar}
        aria-label="Fechar"
      >
        <i className="far fa-times" aria-hidden="true" />
      </button>
    </li>
  )
}

export function ContainerDeToasts(): JSX.Element | null {
  const toasts = useSyncExternalStore(inscrever, obterToasts)

  if (toasts.length === 0) return null

  return createPortal(
    <ul className={classes.container} aria-live="polite">
      {toasts.map(toast => (
        <ItemDeToast key={toast.id} toast={toast} />
      ))}
    </ul>,
    document.body
  )
}
